@if (uiState == uiStateEnumForTemplate.ShowLoading) {
  <div class="center-margin">
    <mat-spinner></mat-spinner>
  </div>
} @else {
  @if (roleWithPermissions) {
    <c-container class="mt-3">
      <button mat-raised-button
              class="margin-bottom-med margin-top-med"
              data-testid="back-to-manage-roles-button"
              routerLink="/manage-global-permissions">
        <mat-icon>arrow_back_ios</mat-icon>
        Back to Roles
      </button>

      <c-row>
        <c-col>
          <h2 data-testid="role-name">{{ roleWithPermissions.Role.Name }}</h2>
        </c-col>
      </c-row>

      <c-row class="mb-3">
        <c-col md="3" class="mb-3">
          <div cListGroup>
            <button cListGroupItem
                    data-testid="role-settings-tab"
                    (click)="screenState = screenStateEnum.RoleSettings"
                    [active]="screenState === screenStateEnum.RoleSettings">
              <div>
                <h5 class="mb-1">Role Settings</h5>
                <p>View and edit role details</p>
              </div>
            </button>

            <button cListGroupItem
                    data-testid="permissions-settings-tab"
                    (click)="screenState = screenStateEnum.PermissionsSettings"
                    [active]="screenState === screenStateEnum.PermissionsSettings">
              <div>
                <h5 class="mb-1">Permissions</h5>
                <p>View and manage role permissions</p>
              </div>
            </button>

            <button cListGroupItem
                    data-testid="users-settings-tab"
                    (click)="screenState = screenStateEnum.UsersSettings"
                    [active]="screenState === screenStateEnum.UsersSettings">
              <div>
                <h5 class="mb-1">Users</h5>
                <p>View users assigned to this role</p>
              </div>
            </button>
          </div>
        </c-col>

        <!-- Role Settings -->
        <c-col>
          @switch (screenState) {
            @case (screenStateEnum.RoleSettings) {
              <mat-card>
                
                <mat-card-header class="header-container">
                  <mat-card-title>Role Settings</mat-card-title>
                  <div class="d-flex gap-2">
                    <button *ngIf="isEditingRole" mat-stroked-button color="warn" (click)="cancelEditRole()">Cancel
                    </button>
                    <button mat-raised-button color="primary" (click)="toggleEditRole()" data-testid="edit-role-button">
                      {{ isEditingRole ? 'Save Changes' : 'Edit Role' }}
                    </button>
                  </div>
                </mat-card-header>

                <mat-card-content>
                  <div class="margin-top-large" *ngIf="!isEditingRole; else editRole">
                    <div class="flex-row justify-space-between align-center custom-border-bottom">
                      <div class="flex-33 bold-text">Role Name</div>
                      <div class="flex-66" data-testid="role-settings-name">{{ roleWithPermissions.Role.Name }}</div>
                    </div>

                    <div class="flex-row justify-space-between align-center custom-border-bottom">
                      <div class="flex-33 bold-text">Description</div>
                      <div class="flex-66" data-testid="role-settings-description">{{ roleWithPermissions.Role.Description }}</div>
                    </div>

                    <div class="flex-row justify-space-between align-center custom-border-bottom">
                      <div class="flex-33 bold-text">Is Cross Game Role</div>
                      <div class="flex-66" data-testid="role-settings-is-cross-game">{{ roleWithPermissions.Role.IsCrossGameRole ? 'Yes' : 'No' }}</div>
                    </div>

                    <div class="flex-row justify-space-between align-center">
                      <div class="flex-33 bold-text">Active</div>
                      <div class="flex-66">
                        <mat-icon [class]="roleWithPermissions.Role.Active ? 'green-text' : 'red-text'">
                          {{ roleWithPermissions.Role.Active ? 'check' : 'clear' }}
                        </mat-icon>
                      </div>
                    </div>
                  </div>

                  <!-- Edit Role -->
                  <form [formGroup]="updateRoleForm">
                    <ng-template #editRole>
                      <div class="form-fields-container margin-top-large">

                        <!-- Role name -->
                        <div class="form-field-row">
                          <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Role Name</mat-label>
                            <input formControlName="roleName"
                                   type="text"
                                   matInput
                                   data-testid="edit-role-name-input"
                                   placeholder="Enter role name">
                            @if (roleNameControl.hasError('required')) {
                              <mat-error data-testid="role-name-error">
                                Name is required
                              </mat-error>
                            }
                          </mat-form-field>
                        </div>

                        <!-- Role description -->
                        <div class="form-field-row">
                          <mat-form-field appearance="outline" class="full-width">
                            <mat-label>Role Description</mat-label>
                            <textarea formControlName="roleDescription"
                                      matInput
                                      data-testid="edit-role-description-input"
                                      placeholder="Enter role description"
                                      rows="3"></textarea>
                            @if (roleDescriptionControl.hasError('required')) {
                              <mat-error data-testid="role-description-error">
                                Role description is required
                              </mat-error>
                            }
                          </mat-form-field>
                        </div>

                        <!-- Is Cross Game Role -->
                        <div class="form-field-row">
                          <mat-checkbox formControlName="isCrossGameRole" class="checkbox-container" data-testid="is-cross-game-role-checkbox">
                            Is Cross Game Role
                          </mat-checkbox>
                        </div>
                      </div>
                    </ng-template>
                  </form>
                </mat-card-content>
              </mat-card>
            }

            <!-- Permissions Settings -->
            @case (screenStateEnum.PermissionsSettings) {
              <mat-card>
                <mat-card-header class="header-container">
                  <mat-card-title>Permissions</mat-card-title>
                  <div class="d-flex gap-2">
                    <button *ngIf="isEditingPermissions" mat-stroked-button color="warn"
                            (click)="cancelEditPermissions()">Cancel
                    </button>
                    <button mat-raised-button color="primary" (click)="toggleEditPermissions()" data-testid="edit-permissions-button">
                      {{ isEditingPermissions ? 'Save Changes' : 'Edit Permissions' }}
                    </button>
                  </div>
                </mat-card-header>

                <mat-card-content>
                  <div *ngIf="!isEditingPermissions; else editPermissions">
                    <table mat-table [dataSource]="selectedPermissions" class="margin-top-large" data-testid="permissions-table">

                      <ng-container matColumnDef="value">
                        <th mat-header-cell *matHeaderCellDef>Value</th>
                        <td mat-cell *matCellDef="let permission" [attr.data-testid]="'permission-value-' + permission.Value">
                          {{ permission.Value }}
                        </td>
                      </ng-container>

                      <ng-container matColumnDef="name">
                        <th mat-header-cell *matHeaderCellDef>Name</th>
                        <td mat-cell *matCellDef="let permission" [attr.data-testid]="'permission-name-' + permission.Value">
                          {{ permission.Name }}
                        </td>
                      </ng-container>

                      <ng-container matColumnDef="active">
                        <th mat-header-cell *matHeaderCellDef>Active</th>
                        <td mat-cell *matCellDef="let permission">
                          <mat-icon *ngIf="permission.Active" class="green-text" [attr.data-testid]="'permission-active-' + permission.Value">
                            check_circle
                          </mat-icon>
                          <mat-icon *ngIf="!permission.Active" class="red-text" [attr.data-testid]="'permission-inactive-' + permission.Value">
                            cancel
                          </mat-icon>
                        </td>
                      </ng-container>

                      <tr mat-header-row *matHeaderRowDef="['value', 'name', 'active']"></tr>
                      <tr mat-row *matRowDef="let row; columns: ['value', 'name', 'active'];"></tr>
                    </table>

                    <p *ngIf="selectedPermissions.length === 0" data-testid="no-active-permissions-message">No active permissions.</p>
                  </div>

                  <form [formGroup]="updateRoleForm">
                    <ng-template #editPermissions>
                      <app-permissions-selector
                          [availablePermissions]="availablePermissions"
                          [selectedPermissions]="selectedPermissions"
                          (selectedPermissionsChange)="selectedPermissions = $event">
                      </app-permissions-selector>
                    </ng-template>
                  </form>
                </mat-card-content>
              </mat-card>
            }

            <!-- Users Settings -->
            @case (screenStateEnum.UsersSettings) {
              <mat-card>
                <mat-card-header>
                  <mat-card-title>Users</mat-card-title>
                </mat-card-header>

                <mat-card-content>
                  @if (roleWithPermissions.AdminUsers && roleWithPermissions.AdminUsers.length > 0) {
                    <table mat-table [dataSource]="roleWithPermissions.AdminUsers" class="margin-top-large">

                      <!-- Name Column -->
                      <ng-container matColumnDef="name">
                        <th mat-header-cell *matHeaderCellDef>Name</th>
                        <td mat-cell *matCellDef="let user"> {{ user.FirstName }} {{ user.LastName }}</td>
                      </ng-container>

                      <!-- Email Column -->
                      <ng-container matColumnDef="email">
                        <th mat-header-cell *matHeaderCellDef>Email</th>
                        <td mat-cell *matCellDef="let user"> {{ user.Email }}</td>
                      </ng-container>

                      <!-- Confirmed On Column -->
                      <ng-container matColumnDef="confirmedOn">
                        <th mat-header-cell *matHeaderCellDef>Confirmed On</th>
                        <td mat-cell *matCellDef="let user">
                          {{ user.ConfirmedOn ? (user.ConfirmedOn | date:'medium') : 'User Not Confirmed' }}
                        </td>
                      </ng-container>

                      <!-- Active Status Column -->
                      <ng-container matColumnDef="active">
                        <th mat-header-cell *matHeaderCellDef>Active</th>
                        <td mat-cell *matCellDef="let user">
                          <mat-icon [class]="user.Active ? 'green-text' : 'red-text'">
                            {{ user.Active ? 'check_circle' : 'cancel' }}
                          </mat-icon>
                        </td>
                      </ng-container>

                      <!-- Actions Column -->
                      <ng-container matColumnDef="actions">
                        <th mat-header-cell *matHeaderCellDef>Actions</th>
                        <td mat-cell *matCellDef="let user">
                          <button mat-icon-button [matMenuTriggerFor]="actionsMenu" [matMenuTriggerData]="{ user: user }">
                            <mat-icon>more_vert</mat-icon>
                          </button>
                        </td>
                      </ng-container>

                      <tr mat-header-row *matHeaderRowDef="displayedUsersColumns"></tr>
                      <tr mat-row *matRowDef="let row; columns: displayedUsersColumns;"></tr>
                    </table>
                  }
                  @else {
                    <p class="text-center margin-top-large">No assigned users.</p>
                  }
                </mat-card-content>
              </mat-card>
            }
          }
        </c-col>
      </c-row>
    </c-container>
  }
}

<!-- Actions Dropdown Menu -->
<mat-menu #actionsMenu="matMenu">
  <ng-template matMenuContent let-user="user">
    <button mat-menu-item (click)="viewUserDetails(user)">
      <mat-icon class="dark-grey menu-icon">visibility</mat-icon>
      <span class="dark-grey menu-text">View User Details</span>
    </button>
  </ng-template>
</mat-menu>